import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { EventAvailable as AppointmentIcon, Add as AddIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS = {
  pending: { label: 'Waiting for the clinic', color: 'warning' },
  proposed: { label: 'New time offered', color: 'info' },
  confirmed: { label: 'Confirmed', color: 'success' },
  declined: { label: 'Declined', color: 'default' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`)
  .toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '');
const when = (d, t) => (d ? `${fmtDate(d)}${t ? ` at ${t}` : ''}` : '');

const EMPTY = { clinic_id: '', provider_id: '', reason: '', preferred_date: '', preferred_time: '' };

/**
 * Ask a clinic for a visit, and follow what happens to the request: the clinic confirms
 * it, offers another time (which the patient accepts or declines here), or declines.
 */
export default function Appointments() {
  const [rows, setRows] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/patient/appointments`);
      setRows(data.appointments || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your appointments');
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startRequest = async () => {
    setForm(EMPTY);
    setOpen(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/patient/clinics`);
      const list = data.clinics || [];
      setClinics(list);
      if (list.length === 1) pickClinic(list[0].id);
    } catch {
      setClinics([]);
    }
  };

  const pickClinic = async (clinicId) => {
    setForm((f) => ({ ...f, clinic_id: clinicId, provider_id: '' }));
    setDoctors([]);
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/patient/clinics/${clinicId}/providers`);
      setDoctors(data.providers || []);
    } catch {
      setDoctors([]);
    }
  };

  const submit = async () => {
    if (!form.clinic_id) { toast.error('Choose a clinic'); return; }
    if (!form.reason.trim()) { toast.error('Tell the clinic what the visit is for'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/v1/patient/appointments`, {
        clinic_id: form.clinic_id,
        provider_id: form.provider_id || null,
        reason: form.reason.trim(),
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time.trim() || null,
      });
      toast.success('Request sent — the clinic will get back to you');
      setOpen(false);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : (d || 'Could not send the request'));
    } finally {
      setSaving(false);
    }
  };

  const act = async (row, fn, done) => {
    setBusyId(row.id);
    try {
      await fn();
      toast.success(done);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That did not work — please try again');
    } finally {
      setBusyId(null);
    }
  };
  const respond = (row, accept) => act(row,
    () => axios.put(`${API_URL}/api/v1/patient/appointments/${row.id}/respond`, { accept }),
    accept ? 'Time accepted — your appointment is confirmed' : 'You declined the offered time');
  const cancel = (row) => act(row,
    () => axios.delete(`${API_URL}/api/v1/patient/appointments/${row.id}`),
    'Request cancelled');

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Appointments</Typography>
          <Typography variant="body2" color="text.secondary">
            Ask your clinic for a visit and see their answer here.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={startRequest}>Request a visit</Button>
      </Stack>

      {rows === null ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !rows.length ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <AppointmentIcon color="disabled" sx={{ fontSize: 48 }} />
            <Typography color="text.secondary" sx={{ mt: 1 }}>No appointment requests yet.</Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((r) => {
            const st = STATUS[r.status] || { label: r.status, color: 'default' };
            return (
              <Card key={r.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={700}>
                        {r.clinic_name}{r.provider_name ? ` · ${r.provider_name}` : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{r.reason}</Typography>
                      {r.preferred_date && (
                        <Typography variant="body2" color="text.secondary">
                          You asked for {when(r.preferred_date, r.preferred_time)}
                        </Typography>
                      )}
                      {(r.status === 'confirmed' || r.status === 'proposed') && r.scheduled_date && (
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                          {r.status === 'confirmed' ? 'Booked for ' : 'The clinic offered '}
                          {when(r.scheduled_date, r.scheduled_time)}
                        </Typography>
                      )}
                      {r.staff_note && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>Note from the clinic: {r.staff_note}</Typography>
                      )}
                    </Box>
                    <Chip size="small" label={st.label} color={st.color} />
                  </Stack>
                  {(r.status === 'proposed' || r.status === 'pending') && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      {r.status === 'proposed' && (
                        <>
                          <Button size="small" variant="contained" disabled={busyId === r.id}
                            onClick={() => respond(r, true)}>Accept this time</Button>
                          <Button size="small" disabled={busyId === r.id}
                            onClick={() => respond(r, false)}>Decline</Button>
                        </>
                      )}
                      {r.status === 'pending' && (
                        <Button size="small" color="inherit" disabled={busyId === r.id}
                          onClick={() => cancel(r)}>Cancel request</Button>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Request a visit</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Clinic" margin="normal" value={form.clinic_id}
            onChange={(e) => pickClinic(e.target.value)}
            helperText={!clinics.length ? 'Your clinics appear here once you are registered with one.' : undefined}>
            {clinics.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField select fullWidth label="Doctor (optional)" margin="normal" value={form.provider_id}
            disabled={!form.clinic_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })}>
            <MenuItem value="">Any doctor</MenuItem>
            {doctors.map((d) => <MenuItem key={d.id} value={d.id}>{d.full_name}</MenuItem>)}
          </TextField>
          <TextField fullWidth multiline minRows={2} label="What is the visit for?" margin="normal"
            value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
            inputProps={{ maxLength: 1000 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth type="date" label="Preferred date (optional)" margin="normal"
              InputLabelProps={{ shrink: true }} value={form.preferred_date}
              inputProps={{ min: new Date().toISOString().slice(0, 10) }}
              onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} />
            <TextField fullWidth label="Preferred time (optional)" margin="normal" placeholder="e.g. morning, 10:30"
              value={form.preferred_time} inputProps={{ maxLength: 50 }}
              onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? 'Sending…' : 'Send request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
